/**
 * Modal Component - Wrapper around shadcn/ui Dialog for backward compatibility
 * Uses shadcn/ui Dialog component with improved accessibility and animations.
 *
 * Options:
 *  - size: preset max-width (ignored when fullBleed).
 *  - dismissible (default true): when false, an outside click won't close the
 *    modal (Escape still closes) — for required/first-run dialogs.
 *  - fullBleed: render children with no header chrome/padding so the caller
 *    owns the full layout (e.g. split-pane editors). An sr-only DialogTitle is
 *    still emitted from `title` so the dialog stays accessible.
 */
import { memo } from 'react';
import React from 'react';
import {
  Dialog,
  DialogContent as DialogContentBase,
  DialogHeader,
  DialogTitle as DialogTitleBase,
  DialogDescription as DialogDescriptionBase,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// Cast shadcn/ui components to typed variants (dialog.tsx uses untyped forwardRef)
const DialogContent = DialogContentBase as React.FC<
  React.HTMLAttributes<HTMLDivElement> & {
    onPointerDownOutside?: (e: Event) => void;
    onEscapeKeyDown?: (e: KeyboardEvent) => void;
  }
>;
const DialogTitle = DialogTitleBase as React.FC<React.HTMLAttributes<HTMLHeadingElement>>;
const DialogDescription = DialogDescriptionBase as React.FC<React.HTMLAttributes<HTMLParagraphElement>>;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  description?: React.ReactNode;
  dismissible?: boolean;
  fullBleed?: boolean;
  className?: string;
}

const Modal = memo(({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  description,
  dismissible = true,
  fullBleed = false,
  className,
}: ModalProps) => {
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
  };

  const outsideGuard = dismissible ? {} : { onPointerDownOutside: (e: Event) => e.preventDefault() };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        {...outsideGuard}
        className={fullBleed
          ? cn('p-0 gap-0 flex flex-col overflow-hidden', className)
          : cn(sizeClasses[size], 'max-h-[90vh] flex flex-col overflow-hidden', className)}
      >
        {fullBleed ? (
          <>
            <DialogTitle className="sr-only">{title}</DialogTitle>
            {description != null && <DialogDescription className="sr-only">{description}</DialogDescription>}
            {children}
          </>
        ) : (
          <>
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{title}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
            <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
              {children}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
});

Modal.displayName = 'Modal';

export default Modal;
